import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Award, Target, Zap, Trophy, Users, BarChart3 } from 'lucide-react';
import axios, { API_URL } from '../../utils/axiosConfig';

function PlayerAnalytics({ userId }) {
  const [analytics, setAnalytics] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [userId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, comparisonRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/player/${userId}`),
        axios.get(`${API_URL}/analytics/comparison/${userId}`)
      ]);
      setAnalytics(analyticsRes.data);
      setComparison(comparisonRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-700 mt-3 text-sm">Chargement des statistiques...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-700">Aucune donnée disponible</p>
      </div>
    );
  }

  const { stats, weeklyPerformance, streak, bestPicks } = analytics;

  // Determine streak icon and color
  const getStreakDisplay = () => {
    if (streak.type === 'winning') {
      return {
        icon: <Zap className="w-6 h-6 text-yellow-500" />,
        color: 'bg-yellow-50 border-yellow-200',
        textColor: 'text-yellow-700',
        label: 'Série gagnante',
        emoji: '🔥'
      };
    } else if (streak.type === 'cold') {
      return {
        icon: <TrendingDown className="w-6 h-6 text-blue-500" />,
        color: 'bg-blue-50 border-blue-200',
        textColor: 'text-blue-700',
        label: 'Série sans points',
        emoji: '❄️'
      };
    }
    return {
      icon: <Target className="w-6 h-6 text-gray-500" />,
      color: 'bg-gray-50 border-gray-200',
      textColor: 'text-gray-700',
      label: 'Pas de série',
      emoji: '⏸️'
    };
  };

  const streakDisplay = getStreakDisplay();

  return (
    <div className="space-y-6">
      {/* Key Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Points */}
        <div className="bg-gradient-to-br from-blue-900 to-blue-950 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
            <span className="text-2xl sm:text-3xl font-bold">{stats.total_points}</span>
          </div>
          <p className="text-xs sm:text-sm opacity-90">Total Points</p>
        </div>

        {/* Success Rate */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
            <span className="text-2xl sm:text-3xl font-bold">{stats.success_rate || 0}%</span>
          </div>
          <p className="text-xs sm:text-sm opacity-90">Taux de réussite</p>
        </div>

        {/* Average Points */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
            <span className="text-2xl sm:text-3xl font-bold">{stats.avg_points_per_match}</span>
          </div>
          <p className="text-xs sm:text-sm opacity-90">Moyenne par match</p>
        </div>

        {/* Current Streak */}
        <div className={`rounded-xl p-4 sm:p-6 shadow-lg border ${streakDisplay.color}`}>
          <div className="flex items-center justify-between mb-2">
            {streakDisplay.icon}
            <span className={`text-2xl sm:text-3xl font-bold ${streakDisplay.textColor}`}>
              {streak.current} {streakDisplay.emoji}
            </span>
          </div>
          <p className={`text-xs sm:text-sm ${streakDisplay.textColor} font-medium`}>
            {streakDisplay.label}
          </p>
        </div>
      </div>

      {/* Performance vs Platform Average */}
      {comparison && (
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-200">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-900" />
            Comparaison avec la moyenne
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Average Points Comparison */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs sm:text-sm text-gray-600 mb-2">Moyenne par match</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-blue-900">{comparison.user.user_avg_points}</p>
                  <p className="text-xs text-gray-600">Vous</p>
                </div>
                <div className="text-center">
                  {parseFloat(comparison.user.user_avg_points) > parseFloat(comparison.platform.platform_avg_points) ? (
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  ) : parseFloat(comparison.user.user_avg_points) < parseFloat(comparison.platform.platform_avg_points) ? (
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  ) : (
                    <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xl sm:text-2xl font-bold text-gray-700">{comparison.platform.platform_avg_points}</p>
                  <p className="text-xs text-gray-600">Moyenne</p>
                </div>
              </div>
            </div>

            {/* Success Rate Comparison */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs sm:text-sm text-gray-600 mb-2">Taux de réussite</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-blue-900">{comparison.user.user_success_rate || 0}%</p>
                  <p className="text-xs text-gray-600">Vous</p>
                </div>
                <div className="text-center">
                  {parseFloat(comparison.user.user_success_rate) > parseFloat(comparison.platform.platform_success_rate) ? (
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  ) : parseFloat(comparison.user.user_success_rate) < parseFloat(comparison.platform.platform_success_rate) ? (
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  ) : (
                    <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xl sm:text-2xl font-bold text-gray-700">{comparison.platform.platform_success_rate || 0}%</p>
                  <p className="text-xs text-gray-600">Moyenne</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Performance Chart */}
      {weeklyPerformance && weeklyPerformance.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-200">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Performance par semaine</h3>
          <div className="space-y-2">
            {weeklyPerformance.slice(0, 5).map((week) => (
              <div key={week.week_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    Semaine {week.week_number}
                  </p>
                  <p className="text-xs text-gray-600">{week.matches_played} matchs</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-900">{week.week_points}</p>
                    <p className="text-xs text-gray-600">points</p>
                  </div>
                  <div className="w-16 sm:w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-900 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((week.week_points / 50) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Best NFL Players */}
      {bestPicks && bestPicks.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-200">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-yellow-500" />
            Vos meilleurs choix
          </h3>
          <div className="space-y-3">
            {bestPicks.map((pick, index) => (
              <div key={pick.player_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-gray-200 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-50 text-blue-900'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{pick.player_name}</p>
                    <p className="text-xs text-gray-600">{pick.position} - {pick.team_city} {pick.team_name}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-base sm:text-lg font-bold text-blue-900">{pick.total_tds} TDs</p>
                  <p className="text-xs text-gray-600">{pick.times_picked} choix</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Additional Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600 mb-1">Matchs prédits</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_predictions}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600 mb-1">Prédictions gagnantes</p>
          <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.winning_predictions}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600 mb-1">Meilleure performance</p>
          <p className="text-xl sm:text-2xl font-bold text-blue-900">{stats.best_match} pts</p>
        </div>
      </div>
    </div>
  );
}

export default PlayerAnalytics;
